export const translations = {
  en: {
    // Navbar
    nav: {
      solution: "Solution",
      forHR: "For HR",
      forManagers: "For Managers",
      pilotProgram: "Pilot Program",
      trustSecurity: "Trust & Security",
      faq: "FAQ",
      requestDemo: "See what Cayus would measure",
    },

    // Hero Section
    hero: {
      badge: "Works inside Microsoft 365, Google Workspace and Slack",
      headline: "Your KPIs measure performance.",
      headlineHighlight: "Cayus measures the leadership behind it.",
      qualifications: [
        { target: "HR", goal: "who want hard data" },
        { target: "managers", goal: "who want to grow" },
        { target: "every company", goal: "that wants to turn its management culture into a performance driver" },
      ],
      subheadline: "Turnover, disengagement, burnout: it all traces back to management. Cayus makes it measurable, continuously, directly within the tools your managers already use: Microsoft 365, Google Workspace, Slack.",
      tagline: "What gets measured gets improved.",
      cta: "See what Cayus would measure in your organisation",
      ctaSub: "A 30-minute conversation. No commitment.",
      secondaryCta: "See how it works",
      trustEuAiAct: "EU AI Act Ready",
      trustPrivacy: "Private by design",
      trustTeams: "Microsoft Teams",
    },

    // Social Proof Section (EN)
    socialProof: {
      headline: "2 organisations have already joined the pilot.",
      subheadline: "Here's what convinced them.",
      points: [
        "Seeing what an annual survey never shows",
        "Numbers you can defend in the boardroom",
        "A frictionless deployment within Microsoft 365",
      ],
    },

    // Insight Section (new)

    // Problem Section

    // ROI Section
    roi: {
      tagline: "The Cost of Inaction",
      headline: "When a good person leaves, it is almost always because of their manager.",
      subheadline: "And it costs more than you think.",
      stats: [
        {
          value: "9.7%",
          label: "of departures are down to management. It was 7.4% in 2022.",
          source: "Source: Work Institute, 2025 Retention Report",
        },
        {
          value: "75%",
          label: "of employee departures are avoidable.",
          source: "Source: Work Institute, 2025",
        },
        {
          value: "€35K+",
          label: "to replace one person: recruitment, training, lost time.",
          source: "Source: Oxford Economics",
        },
      ],
      logic: {
        step1: "Better leadership visibility",
        step2: "Early risk detection",
        step3: "Fewer regretted departures",
        step4: "Protected revenue",
      },
      savings: "€60,000–€150,000 saved per year",
      savingsNote: "That's what 1% less unwanted turnover means for a 250-person organisation.",
      bottomLine: "The real cost isn't the software. It's another year flying blind, discovering departures when it's already too late.",
    },

    // Diagnostic Section
    diagnostic: {
      tagline: "Express Diagnostic",
      headline: "How is management going in your company?",
      subheadline: "5 questions, 2 minutes. See where management stands today, and where to start.",
      startCta: "Start the diagnostic",
      questionLabel: "Question",
      questions: [
        { dimension: "Goals", question: "If you asked a random team member which goals their manager has set for them, what would they say?", options: [
          { t: "They'd mention broad themes, with no clear deadlines", v: 1 },
          { t: "They'd list them precisely, deadlines included, just like their manager", v: 3 },
          { t: "Hard to say... they'd have to dig out their annual review", v: 0 },
          { t: "They'd know them, but their version would differ from their manager's", v: 2 },
        ] },
        { dimension: "Feedback", question: "Think back to the last feedback one of your managers gave their team. It was most likely...", options: [
          { t: "A real conversation, but mostly when something went wrong", v: 2 },
          { t: "At the annual review, that's what it's for", v: 0 },
          { t: "A regular check-in, with as much recognition as improvement points", v: 3 },
          { t: "An off-the-cuff remark, in passing", v: 1 },
        ] },
        { dimension: "Decisions", question: "An important decision was made in a meeting 3 weeks ago. Today...", options: [
          { t: "It's logged in the action plan register: owner, deadline, follow-up scheduled", v: 3 },
          { t: "Minutes were sent, but everyone assumes someone else is handling it", v: 1 },
          { t: "It's in an action plan, but with no firm deadline", v: 2 },
          { t: "The topic is back on the table. No minutes were taken, everyone has their own version", v: 0 },
        ] },
        { dimension: "Measurement", question: "One of your managers has been weakening their team for months. How do you find out?", options: [
          { t: "When someone resigns, and even then, rarely for the real reason", v: 0 },
          { t: "In the annual engagement survey, without certainty", v: 2 },
          { t: "Through corridor talk reaching HR", v: 1 },
          { t: "Our indicators flag it before the team disengages", v: 3 },
        ] },
        { dimension: "Development", question: "After a management training, what actually happens?", options: [
          { t: "An action plan, but nobody measures whether it's applied", v: 2 },
          { t: "Concrete follow-up: we track whether practices actually change", v: 3 },
          { t: "A few good resolutions that fade within weeks", v: 1 },
          { t: "Honestly? Back to business as usual the next day", v: 0 },
        ] },
      ],
      results: {
        title: "Where your management stands today",
        levels: [
          { name: "Emerging", description: "Management runs on instinct. Behaviours are invisible and risks surface only when someone resigns. The good news: every improvement will be felt immediately." },
          { name: "Establishing", description: "Good practices exist, but they depend on each manager's personality. Without measurement, you can't replicate what works or catch what's going wrong." },
          { name: "Consistent", description: "Processes are in place, but you still measure opinions, not behaviours. The next step: objective, continuous data." },
          { name: "Multiplying", description: "You're ahead of the pack. Cayus can automate your measurement and give every manager a personal coach inside their daily tools." },
        ],
        dimensionsTitle: "Breakdown by dimension",
        emailTitle: "Get your detailed analysis by email",
        emailPlaceholder: "Your work email",
        emailCta: "Send my analysis",
        emailSuccess: "Thank you! Your analysis is on its way.",
        restartCta: "Retake the diagnostic",
        demoCta: "See what Cayus would measure",
      },
    },

    // Blog Preview Section
    blogPreview: {
      tagline: "Articles",
      headline: "Latest from the blog",
      subheadline: "Analysis and perspectives on leadership measurement.",
      viewAll: "View all articles",
      readMore: "Read article",
    },

    // Differentiator Section

    // Solution Section

    // Executive/CFO Section

    // HR Benefits Section

    // Manager Benefits Section

    // Signature phrase

    // Final message
    finalMessage: {
      line1: "A great manager isn't just something you feel.",
      line2: "It's something you can see.",
      highlight: "Cayus gives you the tools to measure it.",
    },

    // Pilot Section
    pilot: {
      badge: "3 spots remaining out of 5",
      headline: "Join the first five companies",
      subheadline: "We are working with five organisations to build Cayus with them. This is not a test: you get value from the first weeks.",
      spotsTotal: 5,
      spotsTaken: 2,
      spotsLabel: "spots taken",
      whatYouGet: "What You'll Get",
      benefits: [
        "Concrete observations from the first week",
        "A clear overview of management across your organisation",
        "Coaching inside Teams, as the work happens",
        "6-week behavioural improvement report",
        "A clear picture of your organisation's managerial practices at 3–6 months",
      ],
      parameters: "Pilot Parameters",
      params: [
        { label: "Number of managers", value: "5 managers minimum" },
        { label: "Time to visible results", value: "~6 weeks" },
        { label: "Partnership duration", value: "~6 months" },
      ],
      cta: "Apply for the pilot",
      ctaSub: "3 spots remaining. Selection within 48h",
    },

    // Timeline Section
    timeline: {
      tagline: "Results Timeline",
      headline: "What to Expect",
      subheadline: "From the first observations to proven savings, here is what Cayus brings at each stage.",
      steps: [
        {
          period: "Weeks 1–6",
          title: "Measurable Improvement",
          description: "Managers improve clarity of goals, decisions, expectations, and feedback, driven by weekly behaviour readings and coaching nudges.",
        },
        {
          period: "Months 3–6",
          title: "Behavioural Intelligence",
          description: "You stop relying on surveys: you see how teams are actually led. HR sees the picture team by team, never person by person.",
        },
        {
          period: "End of Pilot",
          title: "Finally demonstrate measurable leadership impact",
          description: "Connect how teams are led to what happens next in engagement, retention and performance, finally giving HR the evidence to secure investment.",
        },
      ],
    },

    // Trust Section
    trust: {
      tagline: "Privacy & Compliance",
      headline: "Engineered for HR Trust",
      subheadline: "Cayus never reads the content of your messages and never shows a person. Here is exactly what it does, and what it does not.",
      features: [
        {
          title: "Confidential Emails Ignored",
          description: "A message marked confidential is never read.",
        },
        {
          title: "No Full Storage",
          description: "Cayus keeps no conversation. It reads a sentence, takes one observation from it, then deletes it.",
        },
        {
          title: "Each manager alone sees themselves",
          description: "Individual readings remain invisible to HR. They only see department-level aggregates.",
        },
        {
          title: "Secure by Design",
          description: "Runs fully within Microsoft 365, your existing security perimeter.",
        },
      ],
      badges: {
        euAiAct: "Aligned with EU AI Act Principles",
        microsoft: "Microsoft 365 Security Perimeter",
        gdpr: "GDPR Compliant",
      },
      quote: "AI you can explain to your teams without having to justify yourself.",
    },

    // Footer / Demo Form
    footer: {
      tagline: "Get Started",
      headline: "See what Cayus would measure in your organisation",
      subheadline: "See exactly what Cayus would show your managers, your HR team and your leadership team.",
      ctaSub: "A 30-minute conversation. No commitment.",
      benefits: [
        "What your managers would see in their Teams tab",
        "Example screens (HR view, by groups)",
        "How better management turns into people staying, and what that saves",
      ],
      form: {
        firstName: "First Name",
        lastName: "Last Name",
        email: "Work Email",
        company: "Company",
        jobTitle: "Job Title",
        companySize: "Company Size",
        selectSize: "Select size",
        message: "Message (Optional)",
        messagePlaceholder: "Tell us about your leadership development challenges...",
        submit: "See what Cayus would measure",
        submitting: "Submitting...",
      },
      companySizes: [
        "1-50 employees",
        "50-100 employees",
        "100-250 employees",
        "250-500 employees",
        "500-1000 employees",
        "1000-2000 employees",
        "2000+ employees",
      ],
      success: {
        title: "Thank You!",
        message: "We'll be in touch within 24 hours to schedule your Leadership ROI Preview.",
      },
      links: {
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        contact: "Contact",
      },
      copyright: "© {year} Cayus AI. All rights reserved.",
    },

    // FAQ Section
    faq: {
      tagline: "FAQ",
      headline: "Frequently Asked Questions",
      subheadline: "Everything you need to know about Cayus.",
      questions: [
        {
          question: "Can HR see what one particular manager does?",
          answer: "No, never. HR only sees groups of eight people minimum. What a manager sees stays with them.",
        },
        {
          question: "What data does Cayus analyse?",
          answer: "How people exchange in Teams, email and meetings: who talks to whom, how often, how clearly. Never the content, and never messages marked confidential.",
        },
        {
          question: "Is Cayus compliant with GDPR and EU AI Act?",
          answer: "Yes. No data is sold, no conversation is kept, and any manager can opt out in one click.",
        },
        {
          question: "How long does it take to see results?",
          answer: "A manager sees first progress in six weeks. HR sees team trends in three to six months.",
        },
        {
          question: "Does Cayus require a separate platform?",
          answer: "No. Cayus is a tab inside Teams. No new password, no new tool to learn.",
        },
      ],
    },
  },

  fr: {
    // Navbar
    nav: {
      solution: "Solution",
      forHR: "Pour les RH",
      forManagers: "Pour les Managers",
      pilotProgram: "Programme Pilote",
      trustSecurity: "Sécurité & Conformité",
      faq: "FAQ",
      requestDemo: "Voir ce que Cayus mesurerait chez vous",
    },

    // Hero Section
    hero: {
      badge: "Fonctionne dans Microsoft 365, Google Workspace et Slack",
      headline: "Vos KPIs mesurent la performance.",
      headlineHighlight: "Cayus mesure le leadership qui la produit.",
      qualifications: [
        { target: "les RH", goal: "qui veulent des données factuelles" },
        { target: "les managers", goal: "qui veulent progresser" },
        { target: "toutes les entreprises", goal: "qui veulent transformer leur culture managériale en levier de performance" },
      ],
      subheadline: "Départs, désengagement, épuisement : presque toujours, le management est en cause. Cayus le rend visible, semaine après semaine, dans les outils que vos managers utilisent déjà.",
      tagline: "Ce qui se mesure s'améliore.",
      cta: "Voir ce que Cayus mesurerait chez vous",
      ctaSub: "Un échange de 30 minutes. Aucun engagement.",
      secondaryCta: "Voir comment ça marche",
      trustEuAiAct: "Conforme EU AI Act",
      trustPrivacy: "Confidentialité dès la conception",
      trustTeams: "Microsoft Teams",
    },

    // Social Proof Section (FR)
    socialProof: {
      headline: "2 organisations ont déjà rejoint le pilote.",
      subheadline: "Voici ce qui les a convaincues.",
      points: [
        "Voir ce qu'une enquête annuelle ne montre jamais",
        "Des chiffres qu'on peut défendre en comité de direction",
        "Une mise en place sans projet informatique",
      ],
    },

    // Insight Section

    // Problem Section

    // ROI Section
    roi: {
      tagline: "Le coût de l'inaction",
      headline: "Quand quelqu'un de bien s'en va, c'est presque toujours à cause de son manager.",
      subheadline: "Et ça coûte bien plus cher qu'on ne le croit.",
      stats: [
        {
          value: "9,7%",
          label: "des départs sont dus au management. C'était 7,4 % en 2022.",
          source: "Source : Work Institute, Rapport 2025",
        },
        {
          value: "75%",
          label: "des départs auraient pu être évités.",
          source: "Source : Work Institute, 2025",
        },
        {
          value: "35 000 €+",
          label: "pour remplacer une personne : recrutement, formation, temps perdu.",
          source: "Source : Oxford Economics",
        },
      ],
      logic: {
        step1: "Meilleure visibilité du leadership",
        step2: "Détection précoce des risques",
        step3: "Moins de départs regrettés",
        step4: "Revenus protégés",
      },
      savings: "60 000 à 150 000 € économisés / an",
      savingsNote: "C'est ce que vous gagnez en gardant 1 % de personnes en plus, dans une entreprise de 250 salariés.",
      bottomLine: "Le vrai coût n'est pas le logiciel. C'est une année de plus à découvrir les départs quand il est trop tard.",
    },

    // Diagnostic Section
    diagnostic: {
      tagline: "Diagnostic Express",
      headline: "Où en est votre culture managériale ?",
      subheadline: "5 questions, 2 minutes. Vous verrez où en est le management chez vous, et par quoi commencer.",
      startCta: "Lancer le diagnostic",
      questionLabel: "Question",
      questions: [
        { dimension: "Objectifs", question: "Si vous demandiez à un membre d'équipe, au hasard, quels objectifs son manager lui a fixés, que répondrait-il ?", options: [
          { t: "Il citerait de grands thèmes, sans échéances précises", v: 1 },
          { t: "Il les citerait précisément, avec les échéances, comme son manager", v: 3 },
          { t: "Difficile à dire... il faudrait qu'il ressorte son entretien annuel", v: 0 },
          { t: "Il les connaîtrait, mais sa version différerait de celle de son manager", v: 2 },
        ] },
        { dimension: "Feedback", question: "Repensez au dernier feedback qu'un de vos managers a donné à son équipe. C'était probablement...", options: [
          { t: "Un vrai échange, mais surtout quand quelque chose n'allait pas", v: 2 },
          { t: "Lors de l'entretien annuel, c'est le moment prévu pour ça", v: 0 },
          { t: "Un point régulier, avec autant de reconnaissance que de pistes de progrès", v: 3 },
          { t: "Une remarque à chaud, entre deux portes", v: 1 },
        ] },
        { dimension: "Décisions", question: "Une décision importante a été prise en réunion il y a 3 semaines. Aujourd'hui...", options: [
          { t: "Elle est inscrite au registre du plan d'action : responsable, échéance, point d'étape planifié", v: 3 },
          { t: "Un compte rendu a été envoyé, mais chacun pense que quelqu'un d'autre s'en occupe", v: 1 },
          { t: "Elle figure dans un plan d'action, mais sans échéance ferme", v: 2 },
          { t: "Le sujet est revenu sur la table. Sans compte rendu, chacun a sa version", v: 0 },
        ] },
        { dimension: "Mesure", question: "Un de vos managers fragilise son équipe depuis des mois. Comment le découvrez-vous ?", options: [
          { t: "Quand quelqu'un démissionne, et encore, pas toujours pour la vraie raison", v: 0 },
          { t: "Dans l'enquête d'engagement annuelle, sans certitude", v: 2 },
          { t: "Par des bruits de couloir remontés aux RH", v: 1 },
          { t: "Nos indicateurs le signalent avant que l'équipe se désengage", v: 3 },
        ] },
        { dimension: "Développement", question: "Après une formation management, que se passe-t-il concrètement ?", options: [
          { t: "Un plan d'action, mais personne ne mesure s'il est appliqué", v: 2 },
          { t: "Un suivi concret : on observe si les pratiques changent réellement", v: 3 },
          { t: "Quelques bonnes résolutions, qui s'estompent en quelques semaines", v: 1 },
          { t: "Honnêtement ? Retour au quotidien dès le lendemain", v: 0 },
        ] },
      ],
      results: {
        title: "Où en est votre management",
        levels: [
          { name: "Emerging", description: "Le management repose sur l'instinct. Les comportements sont invisibles et les risques n'émergent qu'à la démission. Bonne nouvelle : chaque amélioration aura un impact immédiat." },
          { name: "Establishing", description: "De bonnes pratiques existent, mais elles dépendent de la personnalité de chaque manager. Sans mesure, impossible de répliquer ce qui marche ou de détecter ce qui dérape." },
          { name: "Consistent", description: "Les processus sont en place, mais vous mesurez encore des opinions, pas des comportements. La prochaine étape : des données objectives et continues." },
          { name: "Multiplying", description: "Vous êtes en avance. Cayus peut automatiser votre mesure et offrir à chaque manager un coach personnel dans ses outils du quotidien." },
        ],
        dimensionsTitle: "Détail par dimension",
        emailTitle: "Recevez votre analyse détaillée par email",
        emailPlaceholder: "Votre email professionnel",
        emailCta: "Recevoir mon analyse",
        emailSuccess: "Merci ! Votre analyse est en route.",
        restartCta: "Refaire le diagnostic",
        demoCta: "Voir ce que Cayus mesurerait chez vous",
      },
    },

    // Blog Preview Section
    blogPreview: {
      tagline: "Ressources",
      headline: "Nos derniers articles",
      subheadline: "Analyses et points de vue sur la mesure du leadership.",
      viewAll: "Voir tous les articles",
      readMore: "Lire l'article",
    },

    // Differentiator Section

    // Solution Section

    // Executive/CFO Section

    // HR Benefits Section

    // Manager Benefits Section

    // Signature phrase

    // Final message
    finalMessage: {
      line1: "Un bon manager, ça ne se ressent pas seulement.",
      line2: "Ça se voit.",
      highlight: "Cayus vous donne les outils pour le mesurer.",
    },

    // Pilot Section
    pilot: {
      badge: "3 places restantes sur 5",
      headline: "Rejoignez les 5 premières entreprises",
      subheadline: "Nous accompagnons 5 organisations pour construire Cayus avec elles. Ce n'est pas un test : vous en tirez de la valeur dès les premières semaines.",
      spotsTotal: 5,
      spotsTaken: 2,
      spotsLabel: "places prises",
      whatYouGet: "Ce que vous obtenez",
      benefits: [
        "Des observations concrètes dès la première semaine",
        "Une vue d'ensemble du management de votre organisation",
        "Un accompagnement dans Teams, au fil du travail",
        "Un bilan des progrès au bout de 6 semaines",
        "Une image claire des pratiques de vos managers en 3 à 6 mois",
      ],
      parameters: "Paramètres du pilote",
      params: [
        { label: "Nombre de managers", value: "5 managers minimum" },
        { label: "Premiers résultats", value: "~6 semaines" },
        { label: "Durée du partenariat", value: "~6 mois" },
      ],
      cta: "Candidater au pilote",
      ctaSub: "3 places restantes. Sélection sous 48h",
    },

    // Timeline Section
    timeline: {
      tagline: "Calendrier des résultats",
      headline: "À quoi s'attendre",
      subheadline: "Des premières observations aux économies prouvées, voici ce que Cayus apporte à chaque étape.",
      steps: [
        {
          period: "Semaines 1–6",
          title: "Amélioration mesurable",
          description: "Les managers progressent sur la clarté des objectifs, décisions et feedback, grâce aux lectures hebdomadaires de comportement et aux rappels de coaching.",
        },
        {
          period: "Mois 3–6",
          title: "Intelligence comportementale",
          description: "Vous ne dépendez plus des enquêtes : vous voyez comment les équipes sont réellement managées. Les RH voient service par service, jamais personne par personne.",
        },
        {
          period: "Fin du pilote",
          title: "Démontrez enfin un impact leadership mesurable",
          description: "Établissez le lien entre comportements managériaux et indicateurs d'engagement, rétention et performance, enfin des données factuelles pour sécuriser vos investissements.",
        },
      ],
    },

    // Trust Section
    trust: {
      tagline: "Confidentialité & Conformité",
      headline: "Conçu pour la confiance des RH",
      subheadline: "Cayus ne lit jamais le contenu de vos messages et ne montre jamais une personne. Voici exactement ce qu'il fait, et ce qu'il ne fait pas.",
      features: [
        {
          title: "Les messages confidentiels sont exclus",
          description: "Un message marqué confidentiel n'est jamais lu.",
        },
        {
          title: "Rien n'est conservé",
          description: "Cayus ne garde aucune conversation. Il lit une phrase, en tire une observation, puis l'efface.",
        },
        {
          title: "Chaque manager est seul à se voir",
          description: "Les RH ne voient jamais un manager en particulier, seulement des groupes de 8 personnes minimum.",
        },
        {
          title: "Vos données restent chez vous",
          description: "Tout se passe dans votre Microsoft 365, derrière votre propre sécurité.",
        },
      ],
      badges: {
        euAiAct: "Conforme aux principes EU AI Act",
        microsoft: "Périmètre de sécurité Microsoft 365",
        gdpr: "Conforme RGPD",
      },
      quote: "Une IA que vous pouvez expliquer à vos équipes sans avoir à vous justifier.",
    },

    // Footer / Demo Form
    footer: {
      tagline: "Commencer",
      headline: "Voir ce que Cayus mesurerait chez vous",
      subheadline: "Voyez concrètement ce que Cayus montrerait à vos managers, à vos RH et à votre comité de direction.",
      ctaSub: "Un échange de 30 minutes. Aucun engagement.",
      benefits: [
        "Ce que vos managers verraient dans leur onglet Teams",
        "Exemples d'écrans (vue RH, par groupes)",
        "Comment un meilleur management fait rester les gens, et ce que ça économise",
      ],
      form: {
        firstName: "Prénom",
        lastName: "Nom",
        email: "Email professionnel",
        company: "Entreprise",
        jobTitle: "Fonction",
        companySize: "Taille de l'entreprise",
        selectSize: "Sélectionner",
        message: "Message (Optionnel)",
        messagePlaceholder: "Parlez-nous de vos enjeux de développement du leadership...",
        submit: "Voir ce que Cayus mesurerait chez vous",
        submitting: "Envoi en cours...",
      },
      companySizes: [
        "1-50 collaborateurs",
        "50-100 collaborateurs",
        "100-250 collaborateurs",
        "250-500 collaborateurs",
        "500-1000 collaborateurs",
        "1000-2000 collaborateurs",
        "2000+ collaborateurs",
      ],
      success: {
        title: "Merci !",
        message: "Nous vous recontacterons sous 24h pour planifier votre aperçu ROI Leadership.",
      },
      links: {
        privacy: "Politique de confidentialité",
        terms: "Conditions d'utilisation",
        contact: "Contact",
      },
      copyright: "© {year} Cayus AI. Tous droits réservés.",
    },

    // FAQ Section
    faq: {
      tagline: "FAQ",
      headline: "Questions fréquentes",
      subheadline: "Tout ce qu'il faut savoir sur Cayus et sur la protection de vos données.",
      questions: [
        {
          question: "Les RH peuvent-elles voir ce que fait un manager en particulier ?",
          answer: "Non, jamais. Les RH ne voient que des groupes de 8 personnes minimum. Ce que voit un manager n'est visible que de lui.",
        },
        {
          question: "Quelles données Cayus analyse-t-il ?",
          answer: "La façon d'échanger dans Teams, les emails et les réunions : qui parle à qui, à quel rythme, avec quelle clarté. Jamais le contenu, et jamais les messages marqués confidentiels.",
        },
        {
          question: "Cayus est-il conforme au RGPD et à l'EU AI Act ?",
          answer: "Oui. Aucune donnée n'est vendue, aucune conversation n'est conservée, et chaque manager peut se retirer en un clic.",
        },
        {
          question: "Combien de temps faut-il pour voir des résultats ?",
          answer: "Un manager voit ses premiers progrès en 6 semaines. Une direction voit les tendances de ses équipes en 3 à 6 mois.",
        },
        {
          question: "Cayus nécessite-t-il une plateforme séparée ?",
          answer: "Non. Cayus est un onglet dans Teams. Aucun nouveau mot de passe, aucun outil à apprendre.",
        },
      ],
    },
  },

  es: {
    // Navbar
    nav: {
      solution: "Solución",
      forHR: "Para RRHH",
      forManagers: "Para Managers",
      pilotProgram: "Programa Piloto",
      trustSecurity: "Seguridad y Cumplimiento",
      faq: "FAQ",
      requestDemo: "Ver qué mediría Cayus",
    },

    // Hero Section
    hero: {
      badge: "Integrado de forma nativa en Microsoft 365, Google Workspace y Slack",
      headline: "Sus KPIs miden el rendimiento.",
      headlineHighlight: "Cayus mide el liderazgo que lo produce.",
      qualifications: [
        { target: "los RRHH", goal: "que quieren datos factuales" },
        { target: "los managers", goal: "que quieren progresar" },
        { target: "todas las empresas", goal: "que quieren transformar su cultura gerencial en palanca de rendimiento" },
      ],
      subheadline: "Rotación, desvinculación, agotamiento: todo se remonta a la gestión. Cayus lo hace medible, de forma continua, directamente en las herramientas que sus managers ya utilizan: Microsoft 365, Google Workspace, Slack.",
      tagline: "Lo que se mide, mejora.",
      cta: "Ver qué mediría Cayus en su organización",
      ctaSub: "Una conversación de 30 minutos. Sin compromiso.",
      secondaryCta: "Ver cómo funciona",
      trustEuAiAct: "Cumple EU AI Act",
      trustPrivacy: "Privacidad desde el diseño",
      trustTeams: "Microsoft Teams",
    },

    // Social Proof Section (ES)
    socialProof: {
      headline: "2 organizaciones ya se unieron al piloto.",
      subheadline: "Esto es lo que las convenció.",
      points: [
        "Ver lo que una encuesta anual nunca muestra",
        "Datos de RRHH defendibles en el comité de dirección",
        "Un despliegue sin fricción en Microsoft 365",
      ],
    },

    // Insight Section

    // Problem Section

    // ROI Section
    roi: {
      tagline: "El costo de la inacción",
      headline: "Cuando se va alguien bueno, casi siempre es por su manager.",
      subheadline: "Y cuesta más de lo que crees.",
      stats: [
        {
          value: "9,7%",
          label: "de las salidas se deben al management. En 2022 era el 7,4 %.",
          source: "Fuente: Work Institute, Informe 2025",
        },
        {
          value: "75%",
          label: "de las salidas se podrían haber evitado.",
          source: "Fuente: Work Institute, 2025",
        },
        {
          value: "€35K+",
          label: "para sustituir a una persona: contratación, formación, tiempo perdido.",
          source: "Fuente: Oxford Economics",
        },
      ],
      logic: {
        step1: "Mejor visibilidad del liderazgo",
        step2: "Detección temprana de riesgos",
        step3: "Menos salidas lamentadas",
        step4: "Ingresos protegidos",
      },
      savings: "€60.000–€150.000 ahorrados por año",
      savingsNote: "Eso es lo que significa 1 % menos de rotación no deseada en una organización de 250 personas.",
      bottomLine: "El verdadero costo no es el software. Es otro año navegando a ciegas, descubriendo las salidas cuando ya es demasiado tarde.",
    },

    // Diagnostic Section
    diagnostic: {
      tagline: "Diagnóstico Exprés",
      headline: "¿En qué punto está su cultura de management?",
      subheadline: "5 preguntas, 2 minutos. Verá cómo está el management en su empresa y por dónde empezar.",
      startCta: "Iniciar el diagnóstico",
      questionLabel: "Pregunta",
      questions: [
        { dimension: "Objetivos", question: "¿Cómo fijan los objetivos sus managers?", options: ["Rara vez formalizados", "Una vez al año, en las evaluaciones anuales", "Con regularidad, pero sin seguimiento", "Claros, medibles y con seguimiento continuo"] },
        { dimension: "Feedback", question: "¿Con qué frecuencia dan feedback constructivo sus managers?", options: ["Casi nunca, solo en la evaluación anual", "De manera informal, en reuniones de equipo", "Con regularidad, pero la calidad varía según el manager", "Frecuente, estructurado y cercano"] },
        { dimension: "Decisiones", question: "Cuando se toma una decisión en una reunión, ¿qué pasa después?", options: ["A menudo se olvida o se vuelve a discutir", "Se aplica, pero sin responsable claro", "Se nombra un responsable, el plazo queda difuso", "Quién hace qué y para cuándo: siempre explícito"] },
        { dimension: "Medición", question: "¿Cómo miden hoy la calidad del management?", options: ["No la medimos", "Encuesta anual de compromiso", "Encuestas + entrevistas de salida", "Datos de comportamiento en continuo"] },
        { dimension: "Desarrollo", question: "¿Cómo progresan sus managers?", options: ["Aprenden sobre la marcha", "Formaciones puntuales, sin seguimiento", "Programas regulares, impacto difícil de demostrar", "Coaching continuo basado en datos"] },
      ],
      results: {
        title: "Cómo está el management en su empresa",
        levels: [
          { name: "Emerging", description: "El management se basa en el instinto. Los comportamientos son invisibles y los riesgos solo emergen con una dimisión. La buena noticia: cada mejora tendrá un impacto inmediato." },
          { name: "Establishing", description: "Existen buenas prácticas, pero dependen de la personalidad de cada manager. Sin medición, es imposible replicar lo que funciona o detectar lo que falla." },
          { name: "Consistent", description: "Los procesos existen, pero aún miden opiniones, no comportamientos. El siguiente paso: datos objetivos y continuos." },
          { name: "Multiplying", description: "Van por delante. Cayus puede automatizar su medición y dar a cada manager un coach personal en sus herramientas diarias." },
        ],
        dimensionsTitle: "Detalle por dimensión",
        emailTitle: "Reciba su análisis detallado por email",
        emailPlaceholder: "Su email profesional",
        emailCta: "Recibir mi análisis",
        emailSuccess: "¡Gracias! Su análisis está en camino.",
        restartCta: "Repetir el diagnóstico",
        demoCta: "Ver qué mediría Cayus",
      },
    },

    // Differentiator Section

    blogPreview: {
      tagline: "Análisis",
      headline: "Nuestros últimos artículos",
      subheadline: "Análisis y puntos de vista sobre la medición del liderazgo.",
      viewAll: "Ver todos los artículos",
      readMore: "Leer el artículo",
    },

    // Solution Section

    // Executive/CFO Section

    // HR Benefits Section

    // Manager Benefits Section

    // Signature phrase

    // Final message
    finalMessage: {
      line1: "Un buen manager no solo se siente.",
      line2: "Se ve.",
      highlight: "Cayus le da las herramientas para medirlo.",
    },

    // Pilot Section
    pilot: {
      badge: "3 plazas restantes de 5",
      headline: "Únete al primer piloto de Inteligencia de Liderazgo",
      subheadline: "Estamos seleccionando 5 organizaciones para co-crear el futuro de la medición del liderazgo. Este piloto entrega valor estratégico real. No experimentación.",
      spotsTotal: 5,
      spotsTaken: 2,
      spotsLabel: "plazas ocupadas",
      whatYouGet: "Lo que obtendrás",
      benefits: [
        "Métricas de comportamiento de liderazgo desde el día uno",
        "Una visión clara del management de su organización",
        "Acompañamiento en Teams, sobre el trabajo real",
        "Un balance de los avances a las seis semanas",
        "Una foto clara de las prácticas gerenciales de su organización a 3-6 meses",
      ],
      parameters: "Parámetros del piloto",
      params: [
        { label: "Número de managers", value: "5 managers como mínimo" },
        { label: "Tiempo hasta resultados visibles", value: "~6 semanas" },
        { label: "Duración de la colaboración", value: "~6 meses" },
      ],
      cta: "Candidatarse al piloto",
      ctaSub: "3 plazas restantes. Selección en 48h",
    },

    // Timeline Section
    timeline: {
      tagline: "Cronograma de Resultados",
      headline: "Qué esperar",
      subheadline: "Desde las primeras observaciones hasta el ahorro demostrado, esto es lo que Cayus aporta en cada etapa.",
      steps: [
        {
          period: "Semanas 1–6",
          title: "Mejora medible",
          description: "Los managers mejoran la claridad de objetivos, decisiones, expectativas y feedback, impulsados por las lecturas semanales de comportamiento y los recordatorios de coaching.",
        },
        {
          period: "Meses 3–6",
          title: "Inteligencia comportamental",
          description: "Deja de depender de las encuestas: ve cómo se gestiona de verdad. RRHH lo ve equipo por equipo, nunca persona por persona.",
        },
        {
          period: "Fin del piloto",
          title: "Demuestra finalmente un impacto de liderazgo medible",
          description: "Vincula comportamientos de liderazgo con patrones tempranos de compromiso, retención y rendimiento, finalmente dando a RRHH las evidencias para asegurar inversión.",
        },
      ],
    },

    // Trust Section
    trust: {
      tagline: "Privacidad y Cumplimiento",
      headline: "Diseñado para la confianza de RRHH",
      subheadline: "Cayus nunca lee el contenido de sus mensajes y nunca muestra a una persona. Esto es exactamente lo que hace, y lo que no hace.",
      features: [
        {
          title: "Los mensajes confidenciales se excluyen",
          description: "Un mensaje marcado como confidencial no se lee nunca.",
        },
        {
          title: "No se conserva nada",
          description: "Cayus no guarda ninguna conversación. Lee una frase, saca una observación y la borra.",
        },
        {
          title: "Puntuaciones de managers privadas",
          description: "RRHH nunca ve a un manager concreto, solo grupos de ocho personas como mínimo.",
        },
        {
          title: "Seguro por diseño",
          description: "Funciona completamente dentro de Microsoft 365, tu perímetro de seguridad existente.",
        },
      ],
      badges: {
        euAiAct: "Alineado con los principios del EU AI Act",
        microsoft: "Perímetro de seguridad Microsoft 365",
        gdpr: "Cumple con RGPD",
      },
      quote: "IA ética y transparente diseñada para una adopción responsable por RRHH.",
    },

    // Footer / Demo Form
    footer: {
      tagline: "Empezar",
      headline: "Ver qué mediría Cayus en su organización",
      subheadline: "Vea exactamente qué mostraría Cayus a sus managers, a RRHH y a su comité de dirección.",
      ctaSub: "Una conversación de 30 minutos. Sin compromiso.",
      benefits: [
        "Lo que sus managers verían en su pestaña de Teams",
        "Pantallas de ejemplo (vista de RRHH, por grupos)",
        "Cómo un mejor management hace que la gente se quede, y lo que eso ahorra",
      ],
      form: {
        firstName: "Nombre",
        lastName: "Apellido",
        email: "Email corporativo",
        company: "Empresa",
        jobTitle: "Cargo",
        companySize: "Tamaño de empresa",
        selectSize: "Seleccionar",
        message: "Mensaje (Opcional)",
        messagePlaceholder: "Cuéntanos sobre tus desafíos de desarrollo de liderazgo...",
        submit: "Ver qué mediría Cayus",
        submitting: "Enviando...",
      },
      companySizes: [
        "1-50 empleados",
        "50-100 empleados",
        "100-250 empleados",
        "250-500 empleados",
        "500-1000 empleados",
        "1000-2000 empleados",
        "2000+ empleados",
      ],
      success: {
        title: "¡Gracias!",
        message: "Te contactaremos en 24 horas para programar tu vista previa del ROI de Liderazgo.",
      },
      links: {
        privacy: "Política de Privacidad",
        terms: "Términos de Servicio",
        contact: "Contacto",
      },
      copyright: "© {year} Cayus AI. Todos los derechos reservados.",
    },

    // FAQ Section
    faq: {
      tagline: "FAQ",
      headline: "Preguntas frecuentes",
      subheadline: "Todo lo que necesitas saber sobre Cayus.",
      questions: [
        {
          question: "¿Puede RRHH ver las lecturas individuales de los managers?",
          answer: "No, nunca. RRHH solo ve grupos de ocho personas como mínimo. Lo que ve un manager solo lo ve él.",
        },
        {
          question: "¿Qué datos analiza Cayus?",
          answer: "Cómo se intercambia en Teams, correos y reuniones: quién habla con quién, con qué frecuencia y con qué claridad. Nunca el contenido, y nunca los mensajes confidenciales.",
        },
        {
          question: "¿Cumple Cayus con RGPD y EU AI Act?",
          answer: "Sí. No vendemos ningún dato, no guardamos ninguna conversación, y cualquier manager puede retirarse con un clic.",
        },
        {
          question: "¿Cuánto tiempo se tarda en ver resultados?",
          answer: "Un manager mejora en seis semanas. RRHH ve las tendencias de sus equipos en tres a seis meses.",
        },
        {
          question: "¿Requiere Cayus una plataforma separada?",
          answer: "No. Cayus es una pestaña dentro de Teams. Sin nueva contraseña, sin herramienta nueva que aprender.",
        },
      ],
    },
  },
};

// Blog translations (shared across languages)
export const blogTranslations = {
  en: {
    blog: "Blog",
    backToHome: "Back to home",
    backToBlog: "Back to blog",
    readMore: "Read more",
    publishedOn: "Published on",
    noPosts: "No articles published yet. Check back soon!",
    admin: {
      title: "Blog Administration",
      login: "Admin Access",
      passwordLabel: "Admin password",
      passwordPlaceholder: "Enter admin password",
      enter: "Log in",
      wrongPassword: "Incorrect password",
      newArticle: "New article",
      editArticle: "Edit article",
      articles: "Articles",
      draft: "Draft",
      published: "Published",
      save: "Save",
      saving: "Saving...",
      publish: "Publish",
      unpublish: "Unpublish",
      delete: "Delete",
      deleteConfirm: "Are you sure you want to delete this article?",
      titleLabel: "Title",
      contentLabel: "Content",
      imageLabel: "Cover image",
      uploadImage: "Upload image",
      uploading: "Uploading...",
      noArticles: "No articles yet. Create your first one!",
      savedOk: "Article saved successfully",
      deletedOk: "Article deleted",
      errorSave: "Error saving article",
      errorDelete: "Error saving article",
      langTabs: { en: "English", fr: "French", es: "Spanish" },
      translate: "Translate to other languages",
      translating: "Translating...",
    },
  },
  fr: {
    blog: "Blog",
    backToHome: "Retour à l'accueil",
    backToBlog: "Retour au blog",
    readMore: "Lire la suite",
    publishedOn: "Publié le",
    noPosts: "Aucun article publié pour le moment. Revenez bientôt !",
    admin: {
      title: "Administration du Blog",
      login: "Accès administrateur",
      passwordLabel: "Mot de passe admin",
      passwordPlaceholder: "Entrez le mot de passe",
      enter: "Se connecter",
      wrongPassword: "Mot de passe incorrect",
      newArticle: "Nouvel article",
      editArticle: "Modifier l'article",
      articles: "Articles",
      draft: "Brouillon",
      published: "Publié",
      save: "Enregistrer",
      saving: "Enregistrement...",
      publish: "Publier",
      unpublish: "Dépublier",
      delete: "Supprimer",
      deleteConfirm: "Êtes-vous sûr de vouloir supprimer cet article ?",
      titleLabel: "Titre",
      contentLabel: "Contenu",
      imageLabel: "Image de couverture",
      uploadImage: "Télécharger une image",
      uploading: "Téléchargement...",
      noArticles: "Aucun article. Créez le premier !",
      savedOk: "Artículo guardado correctamente",
      deletedOk: "Article supprimé",
      errorSave: "Erreur lors de l'enregistrement",
      errorDelete: "Erreur lors de la suppression",
      langTabs: { en: "Anglais", fr: "Français", es: "Espagnol" },
      translate: "Traduire dans les autres langues",
      translating: "Traduction en cours...",
    },
  },
  es: {
    blog: "Blog",
    backToHome: "Volver al inicio",
    backToBlog: "Volver al blog",
    readMore: "Leer más",
    publishedOn: "Publicado el",
    noPosts: "No hay artículos publicados todavía. ¡Vuelve pronto!",
    admin: {
      title: "Administración del Blog",
      login: "Acceso administrador",
      passwordLabel: "Contraseña de admin",
      passwordPlaceholder: "Ingrese la contraseña",
      enter: "Iniciar sesión",
      wrongPassword: "Contraseña incorrecta",
      newArticle: "Nuevo artículo",
      editArticle: "Editar artículo",
      articles: "Artículos",
      draft: "Borrador",
      published: "Publicado",
      save: "Guardar",
      saving: "Guardando...",
      publish: "Publicar",
      unpublish: "Despublicar",
      delete: "Eliminar",
      deleteConfirm: "¿Estás seguro de que quieres eliminar este artículo?",
      titleLabel: "Título",
      contentLabel: "Contenido",
      imageLabel: "Imagen de portada",
      uploadImage: "Subir imagen",
      uploading: "Subiendo...",
      noArticles: "No hay artículos. ¡Crea el primero!",
      savedOk: "Artículo guardado con éxito",
      deletedOk: "Artículo eliminado",
      errorSave: "Error al guardar el artículo",
      errorDelete: "Error al eliminar el artículo",
      langTabs: { en: "Inglés", fr: "Francés", es: "Español" },
      translate: "Traducir a otros idiomas",
      translating: "Traduciendo...",
    },
  },
};

export const useBlogTranslation = (language) => {
  return blogTranslations[language] || blogTranslations.en;
};

export const useTranslation = (language) => {
  return translations[language] || translations.en;
};
